import { AxiosError } from 'axios'
import api from '@/lib/api/crate-core-api-client'
import { isUpsellOutcomeAlreadyRecorded } from '@/lib/download-protection/refusals'

/**
 * Reporting back what a member did with the Enterprise modal.
 *
 * The answer is the only thing that lifts an `UPSELL_REQUIRED` refusal: until crate-core
 * has one, the same download keeps being refused. This module owns the call that carries
 * it, so the dialog above only says "they clicked" or "they dismissed it".
 *
 * It lives beside the refusal rather than inside it because `refusals.ts` is pure wire
 * knowledge with no dependencies — the self-check script imports it directly.
 *
 * The path carries no `/api` segment: crate-core serves `routes/api.php` under a
 * configurable prefix that is empty by default, which is why every other call through this
 * client is written the same way.
 */

/** What the member did. The two cases are the whole vocabulary crate-core accepts. */
export type UpsellOutcome = 'clicked' | 'dismissed'

/**
 * The labels on the two buttons, kept together because the copy is expected to change.
 */
export const UPSELL_LABELS = {
  clicked: 'See Enterprise plans',
  dismissed: 'Not now',
} as const satisfies Record<UpsellOutcome, string>

/**
 * Records the outcome against a penalty, and resolves once crate-core has it.
 *
 * A 409 means the outcome was already recorded — a double press, or a second tab answering
 * first. The refusal is lifted either way, so that is a success here rather than something
 * to put in front of the member.
 *
 * Any other failure rejects. The caller retries the download regardless: crate-core refuses
 * it again if the outcome never landed, which brings the modal back rather than leaving the
 * member with a download that silently never starts.
 */
export async function recordUpsellOutcome(penaltyId: number, outcome: UpsellOutcome): Promise<void> {
  try {
    await api.post(`/download-protection/upsell/${penaltyId}`, { outcome })
  } catch (error) {
    if (error instanceof AxiosError && isUpsellOutcomeAlreadyRecorded(error.response?.status)) {
      return
    }

    throw error
  }
}
